import Head from 'next/head';

import { TokenContentFaqType } from '@/lib/api/tokens/types';

import { NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '@/constants/chain';

import { getBaseURL } from '@/utils/environment';

interface MarketData {
  priceUsd?: number;
  priceChange24h?: number;
  volume24h?: number;
  marketCap?: number;
}

interface TokenJsonLdProps {
  tokenName: string;
  tokenSymbol: string;
  tokenAddress: string;
  chainId: number;
  chainName: string;
  tokenLogo?: string | null;
  faqs?: TokenContentFaqType[];
  marketData?: MarketData;
}

interface BreadcrumbListSchema {
  '@type': 'BreadcrumbList';
  'itemListElement': {
    '@type': 'ListItem';
    'position': number;
    'name': string;
    'item'?: string;
  }[];
}

interface FAQPageSchema {
  '@type': 'FAQPage';
  'mainEntity': {
    '@type': 'Question';
    'name': string;
    'acceptedAnswer': {
      '@type': 'Answer';
      'text': string;
    };
  }[];
}

interface ThingSchema {
  '@type': 'Thing';
  'name': string;
  'description': string;
  'identifier': string;
  'url': string;
  'image'?: string;
  'additionalProperty': {
    '@type': 'PropertyValue';
    'name': string;
    'value': string | number;
  }[];
}

type SchemaType = BreadcrumbListSchema | FAQPageSchema | ThingSchema;

interface JsonLdGraph {
  '@context': 'https://schema.org';
  '@graph': SchemaType[];
}

function buildBreadcrumbSchema(
  tokenName: string,
  chainId: number,
  chainName: string,
): BreadcrumbListSchema {
  const baseUrl = getBaseURL();

  return {
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Matcha',
        'item': baseUrl,
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': chainName,
        'item': `${baseUrl}/tokens/${NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chainId]}`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': tokenName,
        // Last item should not have 'item' property per Google guidelines
      },
    ],
  };
}

function buildFAQPageSchema(faqs: TokenContentFaqType[]): FAQPageSchema {
  return {
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        // Strip HTML tags from answer for schema (Google recommends plain text)
        'text': faq.answer.replace(/<[^>]*>/g, ''),
      },
    })),
  };
}

function buildThingSchema(
  tokenName: string,
  tokenSymbol: string,
  tokenAddress: string,
  chainId: number,
  chainName: string,
  tokenLogo?: string | null,
  marketData?: MarketData,
): ThingSchema {
  const baseUrl = getBaseURL();
  const networkPath = NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chainId]?.toLowerCase();
  const tokenUrl = `${baseUrl}/tokens/${networkPath}/${tokenAddress}`;

  const additionalProperties: ThingSchema['additionalProperty'] = [
    {
      '@type': 'PropertyValue',
      'name': 'symbol',
      'value': tokenSymbol,
    },
    {
      '@type': 'PropertyValue',
      'name': 'contractAddress',
      'value': tokenAddress,
    },
    {
      '@type': 'PropertyValue',
      'name': 'blockchain',
      'value': chainName,
    },
    {
      '@type': 'PropertyValue',
      'name': 'chainId',
      'value': chainId,
    },
  ];

  // Add market data properties if available (for AI/semantic value)
  if (marketData?.priceUsd != null && !Number.isNaN(marketData.priceUsd)) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'priceUSD',
      'value': marketData.priceUsd,
    });
  }
  if (marketData?.priceChange24h != null && !Number.isNaN(marketData.priceChange24h)) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'priceChange24h',
      'value': marketData.priceChange24h,
    });
  }
  if (marketData?.volume24h != null && !Number.isNaN(marketData.volume24h)) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'volume24hUSD',
      'value': marketData.volume24h,
    });
  }
  if (marketData?.marketCap != null && !Number.isNaN(marketData.marketCap)) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      'name': 'marketCapUSD',
      'value': marketData.marketCap,
    });
  }

  const schema: ThingSchema = {
    '@type': 'Thing',
    'name': `${tokenName} (${tokenSymbol})`,
    'description': `${tokenName} (${tokenSymbol}) is a cryptocurrency token on the ${chainName} network. Trade ${tokenSymbol} at the best price on Matcha.`,
    'identifier': tokenAddress,
    'url': tokenUrl,
    'additionalProperty': additionalProperties,
  };

  if (tokenLogo) {
    schema.image = tokenLogo;
  }

  return schema;
}

/**
 * Escapes JSON string for safe embedding in a script tag.
 * Prevents XSS by escaping sequences that could break out of the script tag.
 */
function escapeJsonForScriptTag(json: string): string {
  return json.replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
}

/**
 * Renders JSON-LD structured data for token profile pages.
 *
 * Includes:
 * - BreadcrumbList: Always rendered for SERP navigation display
 * - FAQPage: Conditionally rendered when FAQ data exists (semantic/AI value only,
 *   rich snippets deprecated for commercial sites as of Aug 2023)
 * - Thing: Always rendered for AI/LLM discoverability
 */
export function TokenJsonLd({
  tokenName,
  tokenSymbol,
  tokenAddress,
  chainId,
  chainName,
  tokenLogo,
  faqs,
  marketData,
}: TokenJsonLdProps) {
  const breadcrumbSchema = buildBreadcrumbSchema(tokenName, chainId, chainName);
  const thingSchema = buildThingSchema(
    tokenName,
    tokenSymbol,
    tokenAddress,
    chainId,
    chainName,
    tokenLogo,
    marketData,
  );

  const graph: SchemaType[] = [breadcrumbSchema, thingSchema];

  // Only add FAQPage schema if FAQ data exists
  if (faqs && faqs.length > 0) {
    graph.push(buildFAQPageSchema(faqs));
  }

  const jsonLd: JsonLdGraph = {
    '@context': 'https://schema.org',
    '@graph': graph,
  };

  // Escape JSON to prevent XSS from content containing </script> or similar
  const safeJson = escapeJsonForScriptTag(JSON.stringify(jsonLd));

  return (
    <Head>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJson }} />
    </Head>
  );
}
